import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHandlerFn,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpInterceptorFn,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent
} from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import {
  SecurityConfig,
  SecurityConfigInjectionToken
} from '../configuration/security.config';
import { OAuthProvider } from '../providers/oauth-provider.interface';
import { AuthenticationService } from '../services/authentication.service';
import { Jwt } from '../web-tokens/jwt';

/**
 * This interceptor takes the available access
 * token and adds it to the authorization header
 */
@Injectable()
export class OAuthInterceptor implements HttpInterceptor {
  private authenticationService: AuthenticationService | null = null;
  private securityConfiguration: SecurityConfig | null = null;

  constructor(private injector: Injector) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<
    | HttpSentEvent
    | HttpHeaderResponse
    | HttpProgressEvent
    | HttpResponse<any>
    | HttpUserEvent<any>
  > {
    return interceptRequest(req, next.handle, {
      authenticationService: this.authService,
      securityConfig: this.securityConfig
    });
  }

  /**
   * Retrieves the authentication service
   * Note: This cannot be injected into the header due to when the interceptor is created
   */
  private get authService(): AuthenticationService {
    if (this.authenticationService === null) {
      this.authenticationService = this.injector.get(AuthenticationService);
    }

    return this.authenticationService;
  }

  private get securityConfig(): SecurityConfig {
    if (this.securityConfiguration === null) {
      this.securityConfiguration = this.injector.get(
        SecurityConfigInjectionToken
      );
    }

    return this.securityConfiguration;
  }
}

export function oauth2Interceptor(): HttpInterceptorFn {
  return (req, next) => {
    return interceptRequest(req, next, {
      authenticationService: inject(AuthenticationService),
      securityConfig: inject(SecurityConfigInjectionToken)
    });
  };
}

function interceptRequest(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  deps: {
    authenticationService: AuthenticationService;
    securityConfig: SecurityConfig;
  }
): Observable<HttpEvent<unknown>> {
  const provider = getOAuthProvider(deps.authenticationService);
  const requestToForward = assignAccessToken(provider, req, deps.securityConfig);

  return next(requestToForward).pipe(
    // There may be other events besides the response.
    filter(event => event instanceof HttpResponse),
    catchError((error: HttpErrorResponse) => {
      if (
        provider !== null &&
        deps.authenticationService.isAuthenticated &&
        isAllowedDomain(req, deps.securityConfig) &&
        error.status === 401
      ) {
        return deps.authenticationService.refreshSession().pipe(
          switchMap(() => {
            return next(assignAccessToken(provider, req, deps.securityConfig));
          })
        );
      } else {
        return throwError(() => error);
      }
    })
  );
}

function assignAccessToken(
  provider: OAuthProvider | null,
  req: HttpRequest<any>,
  securityConfig: SecurityConfig
): HttpRequest<any> {
  let requestToForward = req;

  if (provider !== null && isAllowedDomain(req, securityConfig)) {
    const accessToken = provider.getAccessToken();
    let token: string | null = null;

    // In webcomponents the instanceof operator may return false due to multiple
    // prototypes being added in different components, this allows us to properly
    // translate the token
    if (accessToken && (<any>accessToken).hasOwnProperty('encoded')) {
      token = (<Jwt>accessToken).encoded;
    } else {
      token = <string>accessToken;
    }

    if (token !== null) {
      let tokenValue = 'Bearer ' + token;
      requestToForward = req.clone({
        setHeaders: { Authorization: tokenValue }
      });
    } else {
      console.info(
        'OAuthInterceptor: User is not authorized, no authorization header provided.'
      );
    }
  }
  return requestToForward;
}

/**
 * Determines if the outgoing request should have the authorization header added to it
 * @param req The outgoing HttpRequest
 */
function isAllowedDomain(
  req: HttpRequest<any>,
  securityConfig: SecurityConfig
): boolean {
  const parser = document.createElement('a');
  parser.href = req.url;
  const hostName = parser.hostname.toLowerCase();
  const allowedDomains = securityConfig.securityDomains;

  for (let domain of allowedDomains) {
    if (domain.toLowerCase() === hostName) {
      console.debug(
        'OAuthInterceptor: White-listed hostname detected for authorization interception. Hostname: ',
        hostName
      );
      return true;
    }
  }

  console.debug(
    'OAuthInterceptor: Unknown hostname detected for authorization interception. Hostname: ',
    hostName,
    '; Allowed Domains: ',
    allowedDomains
  );
  return false;
}

/**
 * Validates and returns an OAuth provider
 * @param value The provider that should be converted
 * @returns The OAuth provider if it is valid, otherwise null
 */
function getOAuthProvider(value: any): OAuthProvider | null {
  if ((<OAuthProvider>value).getAccessToken !== undefined) {
    return <OAuthProvider>value;
  }

  return null;
}
